'use client';
import React from 'react';
import { Icon, IconProp } from '@benzinga/core-ui';
import { SmartLink } from '@benzinga/analytics';
import { faArrowRight } from '@fortawesome/pro-light-svg-icons/faArrowRight';
import { faAngleRight } from '@fortawesome/pro-regular-svg-icons/faAngleRight';
import styled from '@benzinga/themetron';
import classnames from 'classnames';

export interface TipCardProps {
  data: TipCardData;
  variant?: 'default' | 'compact' | 'fusion';
  className?: string;
}

export interface TipCardData {
  icon?: IconProp;
  subtitle: string;
  title: string;
  url: string;
}

export const TipCard: React.FC<TipCardProps> = ({ className, data, variant = 'default' }) => {
  if (variant === 'fusion') {
    return (
      <SmartLink href={data.url}>
        <Container className={classnames(`tip-card ${variant}-variant`, className)}>
          <div className="main-section">
            <div className="icon-wrapper">{data.icon && <Icon className="icon" icon={data.icon} />}</div>
            <h3 className="tip-card-title">{data.title}</h3>
            <p className="tip-card-subtitle">{data.subtitle}</p>
          </div>
          <span className="read-more-button">
            <Icon className="arrow-right-icon" icon={faAngleRight} />
          </span>
        </Container>
      </SmartLink>
    );
  }

  return (
    <Container className={classnames(`tip-card ${variant}-variant`, className)}>
      <div className="main-section">
        {variant === 'default' && (
          <div className="icon-wrapper">{data.icon && <Icon className="icon" icon={data.icon} />}</div>
        )}
        <a href={data.url} rel="noopener noreferrer" target="_blank">
          {/*It is not normal that we use h1 for cards.*/}
          {/*In the page must be only one h1 title*/}
          <h2 className="tip-card-title">{data.title}</h2>
          <p className="tip-card-subtitle">{data.subtitle}</p>
        </a>
      </div>
      <a className="read-more-button" href={data.url} rel="noopener noreferrer" target="_blank">
        {variant === 'default' && <span className="read-more-button-text">READ MORE</span>}
        <Icon className="arrow-right-icon" icon={faArrowRight} />
      </a>
    </Container>
  );
};

const Container = styled.div`
  &.tip-card {
    display: flex;
    flex-direction: column;
    box-shadow: ${({ theme }) => theme.shadow.sm};
    background-color: ${({ theme }) => theme.colorPalette.white};
    border-radius: 0.25rem;
    overflow: auto;

    &.compact-variant {
      flex-direction: row;

      .read-more-button {
        display: block;
        width: 20px;
        background-color: unset;
        margin: 0 1rem;
        margin-top: auto;
      }

      .tip-card-title {
        font-size: ${({ theme }) => theme.fontSize.xl};
      }

      .tip-card-subtitle {
        font-size: ${({ theme }) => theme.fontSize.base};
      }
    }

    .main-section {
      padding: 1rem;
    }

    .icon-wrapper {
      border-radius: ${({ theme }) => theme.borderRadius.full};
      background-color: ${({ theme }) => theme.colorPalette.blue50};
      display: flex;
      justify-content: center;
      align-items: center;
      width: 3.5rem;
      height: 3.5rem;
      margin-bottom: 0.75rem;

      > span {
        color: ${({ theme }) => theme.colorPalette.blue500};
        font-size: ${({ theme }) => theme.fontSize['2xl']};
      }
    }

    .tip-card-title {
      margin-bottom: 0.5rem;
      text-transform: uppercase;
      color: ${({ theme }) => theme.colorPalette.gray800};
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }

    .tip-card-subtitle {
      color: ${({ theme }) => theme.colorPalette.gray500};
    }

    .read-more-button {
      display: flex;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      padding: 0.5rem 1rem;
      background-color: ${({ theme }) => theme.colorPalette.blue50};

      &:hover {
        .arrow-right-icon {
          color: ${({ theme }) => theme.colorPalette.blue500};
        }
      }
    }

    .read-more-button-text {
      text-transform: uppercase;
      color: ${({ theme }) => theme.colorPalette.blue500};
      font-weight: ${({ theme }) => theme.fontWeight.semibold};
    }

    .arrow-right-icon {
      color: ${({ theme }) => theme.colorPalette.gray400};

      &:hover {
        color: ${({ theme }) => theme.colorPalette.blue500};
      }
    }

    &.fusion-variant {
      display: flex;
      flex-direction: row;
      align-items: flex-end;
      border: 1px solid ${({ theme }) => theme.colorPalette.gray100};
      box-shadow: none;
      transition: 0.25s box-shadow ease-in-out;

      .main-section {
        flex: 1;
        height: 100%;
        padding-bottom: 10px;
      }

      .tip-card-title {
        font-size: ${({ theme }) => theme.fontSize.base};
        font-weight: ${({ theme }) => theme.fontWeight.bold};
        transition: 0.25s color ease-in-out;
      }

      .tip-card-subtitle {
        font-size: ${({ theme }) => theme.fontSize.base};
      }

      .read-more-button {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 40px;
        margin: 0 16px 16px;
        background-color: ${({ theme }) => theme.colorPalette.gray100};
        border-radius: 50%;
        fill: ${({ theme }) => theme.colorPalette.gray800};
        transition: 0.25s background-color ease-in-out;

        .arrow-right-icon {
          transition: 0.25s color ease-in-out;
        }
      }

      .icon-wrapper {
        width: 3rem;
        height: 3rem;
        background-color: transparent;
        margin-bottom: 2rem;
        > span {
          font-size: 2.5rem;
        }
      }

      &:hover {
        box-shadow: 0 2px 16px rgba(25, 68, 128, 0.1);

        .tip-card-title {
          color: ${({ theme }) => theme.colorPalette.blue500};
        }

        .read-more-button {
          background-color: ${({ theme }) => theme.colorPalette.blue500};
          .arrow-right-icon {
            color: ${({ theme }) => theme.colorPalette.white};
          }
        }
      }

      @media screen and (max-width: 1280px) {
        flex-direction: column;
        align-items: flex-start;
      }

      @media screen and (max-width: 959px) {
        .read-more-button {
          display: none;
        }
      }
    }
  }
`;
